import { useEffect, useRef, useState } from 'react'
import { NeoniApi } from '../../../../lib/api'
import { getSectors } from '../../../../redux/actions/sectorActions'
import { getSuppliers } from '../../../../redux/actions/suppliersActions'
import { useAppDispatch, useAppSelector } from '../../../../redux/hooks/reduxHooks'
import { SECTOR_RESET, selectAllSector, selectSectorError, selectSectorStatus } from '../../../../redux/slices/sectorSlice'
import { selectAllSuppliers, selectSuppliersError, selectSuppliersPagination, selectSuppliersStatus } from '../../../../redux/slices/suppliersSlice'
import { ApiPagination } from '../../../../types/PaginationApi'
import { FetchStatus } from '../../../../types/LoadingStates'

type SupplierQuery = Omit<ApiPagination, 'orderBy'> & {
	orderBy?: string
	sectorCodes?: string | number
}

export function useSectorOptions(api: NeoniApi, open: boolean) {
	const dispatch = useAppDispatch()
	const status = useAppSelector(selectSectorStatus)
	const error = useAppSelector(selectSectorError)
	const sectors = useAppSelector(selectAllSector)
	const [loadedApi, setLoadedApi] = useState<NeoniApi>()
	const [loadedGeneration, setLoadedGeneration] = useState<number>()
	const controller = useRef<AbortController>()
	const context = useRef({ api, open, generation: 0 })
	if (context.current.api !== api || context.current.open !== open) {
		context.current = { api, open, generation: context.current.generation + 1 }
	}

	const load = () => {
		controller.current?.abort()
		controller.current = new AbortController()
		setLoadedApi(api)
		setLoadedGeneration(context.current.generation)
		dispatch(getSectors(api, { signal: controller.current.signal }))
	}

	useEffect(() => {
		if (open) {
			dispatch(SECTOR_RESET())
			load()
		}
		return () => controller.current?.abort()
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [api, open])

	const current = open && loadedApi === api && loadedGeneration === context.current.generation
	return { current, status, error, sectors: current && status === FetchStatus.loaded ? sectors : [], retry: load }
}

export function useSupplierOptions(api: NeoniApi, open: boolean) {
	const dispatch = useAppDispatch()
	const status = useAppSelector(selectSuppliersStatus)
	const error = useAppSelector(selectSuppliersError)
	const suppliers = useAppSelector(selectAllSuppliers)
	const pagination = useAppSelector(selectSuppliersPagination)
	const [loadedApi, setLoadedApi] = useState<NeoniApi>()
	const [loadedGeneration, setLoadedGeneration] = useState<number>()
	const controller = useRef<AbortController>()
	const requested = useRef<SupplierQuery>({})
	const context = useRef({ api, open, generation: 0 })
	if (context.current.api !== api || context.current.open !== open) {
		context.current = { api, open, generation: context.current.generation + 1 }
	}

	const retry = () => {
		controller.current?.abort()
		controller.current = new AbortController()
		setLoadedApi(api)
		setLoadedGeneration(context.current.generation)
		dispatch(getSuppliers(api, requested.current, { signal: controller.current.signal }))
	}
	const load = (query: SupplierQuery) => {
		requested.current = query
		retry()
	}

	useEffect(() => () => controller.current?.abort(), [api, open])

	const current = open && loadedApi === api && loadedGeneration === context.current.generation
	return {
		current, status, error, pagination, requested: requested.current,
		suppliers: current && status === FetchStatus.loaded ? suppliers : [],
		load, retry,
	}
}
